import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/* ============================================================================
 * Structural / identity primitives — Design Specification v1.1 §2.7 / §9.
 *
 * These are the "build first" components: they carry the identity moves that
 * survive a logo-removed screenshot (v1.1 §0.1 / §12). Everything downstream
 * (Evidence Drawer, Executive OS, engine dashboards) composes from these.
 * All values reference tokens — no literal hex/px lives here.
 * ========================================================================== */

/* ---------------------------------------------------------------------------
 * <TickRule> — the calibration-scaled hairline divider (v1.1 §2.7.2).
 * A hairline rule with 4px ticks at --tick-interval along its leading edge.
 * Structural, never decorative: use only where a section is actually ruled.
 * ------------------------------------------------------------------------- */
export function TickRule({
  orientation = 'horizontal',
  className,
  ...rest
}: { orientation?: 'horizontal' | 'vertical' } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="separator"
      aria-orientation={orientation}
      className={cn(orientation === 'horizontal' ? 'tick-rule' : 'tick-rail', className)}
      {...rest}
    />
  );
}

/* ---------------------------------------------------------------------------
 * <CoordinateMarks> — the corner "+" drafting marks (v1.1 §2.7.2).
 * Rendered ONLY on the three signature surfaces (Evidence Drawer, Executive OS
 * hero, Engine Dashboard header). The parent must be `position: relative`.
 * ------------------------------------------------------------------------- */
export function CoordinateMarks({ inset = '0.375rem' }: { inset?: string }) {
  const base =
    'pointer-events-none absolute select-none text-[10px] leading-none font-mono';
  const style = { color: 'var(--coordinate-color)' } as const;
  return (
    <div aria-hidden>
      <span className={base} style={{ ...style, top: inset, left: inset }}>+</span>
      <span className={base} style={{ ...style, top: inset, right: inset }}>+</span>
      <span className={base} style={{ ...style, bottom: inset, left: inset }}>+</span>
      <span className={base} style={{ ...style, bottom: inset, right: inset }}>+</span>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * <SplitPane> — the locked 63:37 two-pane proportion (v1.1 §2.7.1).
 * Every two-pane signature surface splits at this single, always-repeated ratio,
 * content-heavy side on the reading-leading edge. Driven by --split-* tokens.
 * ------------------------------------------------------------------------- */
export function SplitPane({
  primary,
  secondary,
  orientation = 'horizontal',
  className,
}: {
  primary: ReactNode;
  secondary: ReactNode;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}) {
  const style =
    orientation === 'horizontal'
      ? { gridTemplateColumns: 'var(--split-primary) var(--split-secondary)' }
      : { gridTemplateRows: 'var(--split-primary) var(--split-secondary)' };
  return (
    <div className={cn('grid min-w-0', className)} style={style}>
      <div className="min-w-0">{primary}</div>
      <div className="min-w-0">{secondary}</div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * <LedgerRow> — rule-separated dense row; the DEFAULT over "card" (v1.1 §2.7.2).
 * Optional mono index column (neutral-6) reinforces "instrumented data".
 * ------------------------------------------------------------------------- */
export function LedgerRow({
  index,
  children,
  className,
  last = false,
  ...rest
}: {
  index?: number | string;
  last?: boolean;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 py-2.5',
        !last && 'border-b border-border-subtle',
        className,
      )}
      {...rest}
    >
      {index != null && (
        <span
          className="mt-0.5 w-6 shrink-0 select-none text-right font-mono text-[11px] tabular-nums text-neutral-6"
          data-tabular
          aria-hidden
        >
          {index}
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * <InstrumentBezelIcon> — the dial-face icon frame (v1.1 §2.4.1).
 * A thin (1px) circular bezel + a single calibration notch at 12 o'clock,
 * wrapping any of the 12 engine glyphs. On active: bezel/glyph tint to the
 * engine accent + notch becomes the "needle". On the Copilot: the notch is
 * Signal Copper permanently — the one icon that is always "on".
 * ------------------------------------------------------------------------- */
export function InstrumentBezelIcon({
  glyph,
  active = false,
  copperNotch = false,
  size = 24,
  className,
}: {
  glyph: ReactNode;
  active?: boolean;
  /** render the notch in Signal Copper permanently (Copilot "always on"). */
  copperNotch?: boolean;
  size?: number;
  className?: string;
}) {
  const notchStroke = copperNotch
    ? 'rgb(var(--ai-accent))'
    : active
      ? 'rgb(var(--engine-accent))'
      : 'currentColor';
  return (
    <span
      className={cn(
        'relative inline-flex items-center justify-center transition-colors duration-fast ease-calibration',
        active ? 'text-engine' : 'text-neutral-6',
        className,
      )}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        className="absolute inset-0"
      >
        <circle cx="12" cy="12" r="10.5" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        <line x1="12" y1="1.5" x2="12" y2="4.5" strokeWidth="1.5" stroke={notchStroke} />
      </svg>
      <span
        className="relative leading-none"
        style={{ fontSize: Math.round(size * 0.42) }}
      >
        {glyph}
      </span>
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * <EyebrowLabel> — the signature pairing (v1.1 §2.2.1): a tracked mono,
 * small-caps micro-label ALWAYS sitting directly above a serif headline.
 * Never a mono label alone, never a sans label above a serif headline — this
 * exact combination is the closest thing the system has to a wordmark.
 * ------------------------------------------------------------------------- */
export function EyebrowLabel({
  label,
  title,
  size = 'md',
  accent = 'neutral',
  as,
  className,
  titleClassName,
}: {
  label: string;
  title: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** eyebrow tint — engine identity, AI copper, or neutral chrome. */
  accent?: 'neutral' | 'engine' | 'ai';
  as?: ElementType;
  className?: string;
  titleClassName?: string;
}) {
  const Title = (as ?? 'h2') as ElementType;
  const titleSize =
    size === 'lg' ? 'text-display-lg' : size === 'sm' ? 'text-display-md' : 'text-heading-lg';
  const eyebrowTint =
    accent === 'engine' ? 'text-engine' : accent === 'ai' ? 'text-ai' : 'text-text-tertiary';
  return (
    <div className={className}>
      <div
        className={cn(
          'font-mono text-[11px] font-semibold uppercase leading-none',
          eyebrowTint,
        )}
        style={{ letterSpacing: '0.16em' }}
      >
        {label}
      </div>
      <Title
        className={cn(
          'mt-1.5 font-display font-semibold tracking-tight text-text-display',
          titleSize,
          titleClassName,
        )}
      >
        {title}
      </Title>
    </div>
  );
}
